import { useQuery } from "@tanstack/react-query";
import { getTokenAsync } from "@coworker/app/src/hooks/API/getAuthToken";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { AtkomstRolesResponse } from "@coworker/apprestructured/src/shared/types/atkomstRoles";

const atkomstUrl = "https://api.ingka.ikea.com/auth-service";

const normalizeStoreIdForAtkomst = (storeId: string) =>
  storeId.split("#")[0] ?? storeId;

const getUserPermissionsForStore = async (
  storeId: string
): Promise<string[]> => {
  const token = await getTokenAsync();
  if (!token) {
    throw new Error("No token available");
  }

  const url = `${atkomstUrl}/clients/STO.${storeId}/roles?clientId=STO.${storeId}&service=FIXA`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(
      `Atkomst roles fetch failed: ${response.status} ${response.statusText}`
    );
  }

  const data: AtkomstRolesResponse = await response.json();

  if (!data || !data.results) {
    return [];
  }

  const permissions = [
    ...new Set(
      data.results.flatMap((role) => role.permissions.map((p) => p.name))
    ),
  ];
  return permissions;
};

const useUserAtkomstPermissions = (storeId?: string) => {
  const loggedInStoreId = useStoreId();
  const requestedStoreId = storeId || loggedInStoreId;
  const atkomstStoreId = requestedStoreId
    ? normalizeStoreIdForAtkomst(requestedStoreId)
    : "";

  return useQuery({
    queryKey: ["userAtkomstRoles", atkomstStoreId],
    queryFn: () => getUserPermissionsForStore(atkomstStoreId),
    enabled: !!atkomstStoreId,
    staleTime: 60 * 60 * 1000 * 24, // 1 day
    retry: false,
  });
};

export default useUserAtkomstPermissions;
